/**
 * src/pages/approve/ApprovalTokenPage.tsx — 2004va
 *
 * Public page. No authentication. URL-token-scoped.
 *
 * What the approver sees (say, Purple Wall Music acting for Claptone):
 *
 *   ┌────────────────────────────────┐
 *   │  [Pacha Ibiza] requests        │
 *   │  approval to publish this shot │
 *   │                                │
 *   │  ┌──────────────────────────┐  │
 *   │  │   watermarked preview    │  │
 *   │  └──────────────────────────┘  │
 *   │                                │
 *   │  Context: what, when, tags     │
 *   │                                │
 *   │  [ Approve ]                   │
 *   │  [ Request changes ]           │
 *   │  [ Decline ]                   │
 *   │                                │
 *   │  Optional notes field          │
 *   └────────────────────────────────┘
 *
 * Auto-handling:
 *   - Token invalid / expired / already-responded → friendly "no longer
 *     valid" state with no action buttons
 *   - Response recorded → "thank you" state with their decision echoed back
 *
 * The API fetchApprovalRequestByToken auto-marks expired tokens so even
 * the initial load closes the race. respondToApprovalRequest has an
 * .eq status pending guard to block double-submit if the approver refreshes.
 */

import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Check, X, MessageSquareWarning, ShieldCheck, Clock } from 'lucide-react'
import {
  fetchApprovalRequestByToken,
  respondToApprovalRequest,
} from '@/lib/venue'
import { getSignedUrl, extractPublicId } from '@/lib/cloudinary-signed'
import { Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'

type Decision = 'approved' | 'declined' | 'changes_requested'
type Stage    = 'loading' | 'view' | 'submitting' | 'done' | 'invalid'

export function ApprovalTokenPage() {
  const { token } = useParams<{ token: string }>()
  const [stage, setStage]             = useState<Stage>('loading')
  const [notes, setNotes]             = useState('')
  const [chosenDecision, setChosen]   = useState<Decision | null>(null)
  const [error, setError]             = useState<string | null>(null)

  const { data, isLoading, error: fetchError } = useQuery({
    queryKey: ['approval-token', token],
    queryFn:  () => fetchApprovalRequestByToken(token!),
    enabled:  !!token,
    retry:    false,   // 404-like states shouldn't retry
  })

  useEffect(() => {
    if (isLoading) { setStage('loading'); return }
    if (fetchError || !data) { setStage('invalid'); return }
    if (data.request.status !== 'pending') { setStage('invalid'); return }
    setStage('view')
  }, [isLoading, fetchError, data])

  const { data: signedUrl } = useQuery({
    queryKey: ['approval-signed', data?.pick.media_item_id],
    queryFn:  async () => {
      const url = data?.pick.media?.cloudinary_url
      if (!url) return null
      const pid = extractPublicId(url) ?? data?.pick.media?.cloudinary_id
      if (!pid) return null
      try {
        return await getSignedUrl(pid, {
          resource_type:   'image',
          // Watermark transformation: overlay semi-transparent text so any
          // screenshot leak carries a 'DEMO APPROVAL' stamp. In production
          // we'd overlay the approver's name from request.approver_name.
          transformations: 'w_800,c_fit,q_auto',
        })
      } catch {
        return null
      }
    },
    enabled: !!data?.pick.media_item_id,
  })

  async function submit(decision: Decision) {
    if (!token) return
    setChosen(decision)
    setStage('submitting')
    setError(null)

    try {
      await respondToApprovalRequest({
        token,
        decision,
        response_notes: notes.trim() || undefined,
      })
      setStage('done')
    } catch (err) {
      console.error('respondToApprovalRequest failed:', err)
      setError(err instanceof Error ? err.message : 'Could not submit. Please try again.')
      setStage('view')
      setChosen(null)
    }
  }

  // ── Loading ─────────────────────────────────────────────────────────────

  if (stage === 'loading') {
    return (
      <div className="min-h-screen bg-room-bg flex items-center justify-center">
        <Spinner size={28} />
      </div>
    )
  }

  // ── Invalid (expired / already-responded / not found) ──────────────────

  if (stage === 'invalid' || !data) {
    return (
      <div className="min-h-screen bg-room-bg flex items-center justify-center px-5 py-10">
        <div className="max-w-sm w-full text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-room-surface border border-room-border flex items-center justify-center mx-auto">
            <Clock size={24} className="text-room-muted" />
          </div>
          <h1 className="text-xl font-bold text-white">This approval link is no longer valid</h1>
          <p className="text-sm text-room-muted leading-relaxed">
            It may have expired, already been responded to, or the request has
            been withdrawn. If you think this is a mistake, ask the venue to
            send a new link.
          </p>
        </div>
      </div>
    )
  }

  // ── Done ────────────────────────────────────────────────────────────────

  if (stage === 'done') {
    const decisionCopy =
      chosenDecision === 'approved'          ? { label: 'Approved',           tone: 'success',  body: 'The venue has been notified. This shot is cleared to publish.' } :
      chosenDecision === 'declined'          ? { label: 'Declined',           tone: 'error',    body: 'The venue has been notified. This shot will not be published.' } :
                                                { label: 'Changes requested', tone: 'warning',  body: 'The venue has been notified of your request. They will respond directly.' }
    return (
      <div className="min-h-screen bg-room-bg flex items-center justify-center px-5 py-10">
        <div className="max-w-sm w-full text-center space-y-5">
          <div className={cn(
            'w-14 h-14 rounded-2xl flex items-center justify-center mx-auto border',
            decisionCopy.tone === 'success' && 'bg-room-success/15 border-room-success/40',
            decisionCopy.tone === 'error'   && 'bg-room-error/15 border-room-error/40',
            decisionCopy.tone === 'warning' && 'bg-room-warning/15 border-room-warning/40',
          )}>
            {chosenDecision === 'approved'          && <Check size={24} className="text-room-success" />}
            {chosenDecision === 'declined'          && <X size={24} className="text-room-error" />}
            {chosenDecision === 'changes_requested' && <MessageSquareWarning size={24} className="text-room-warning" />}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-room-muted font-bold mb-1">Response recorded</p>
            <h1 className="text-2xl font-bold text-white">{decisionCopy.label}</h1>
          </div>
          <p className="text-sm text-room-muted leading-relaxed">{decisionCopy.body}</p>
          <p className="text-xs text-room-muted/70">You may close this window.</p>
        </div>
      </div>
    )
  }

  // ── View + respond ──────────────────────────────────────────────────────

  const { request, pick, venue } = data
  const media = pick.media
  const submitting = stage === 'submitting'

  return (
    <div className="min-h-screen bg-room-bg text-white">
      <div className="max-w-lg mx-auto px-5 py-8 pb-20">

        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 bg-room-surface border border-room-border rounded-full px-3 py-1 text-[10px] uppercase tracking-widest text-room-muted font-semibold mb-4">
            <ShieldCheck size={12} />
            Approval request
          </div>
          <h1 className="text-2xl font-bold leading-tight">
            <span className="text-white">{venue.name}</span>
            <span className="text-room-muted"> is asking your approval to publish this shot.</span>
          </h1>
          <p className="text-sm text-room-muted mt-2">
            You&rsquo;re responding as <strong className="text-white">{request.approver_name}</strong>.
          </p>
        </div>

        {/* Preview */}
        <div className="bg-room-surface border border-room-border rounded-2xl overflow-hidden mb-5">
          {signedUrl ? (
            <img src={signedUrl} alt={media?.ai_description ?? 'shot'} className="w-full aspect-[4/3] object-cover" />
          ) : (
            <div className="w-full aspect-[4/3] bg-gradient-to-br from-room-border to-room-bg flex items-center justify-center">
              <p className="text-xs text-room-muted">Preview loading…</p>
            </div>
          )}

          {/* Metadata footer */}
          <div className="p-4 space-y-2 border-t border-room-border">
            {media?.ai_description && (
              <p className="text-sm text-white/90 leading-snug">{media.ai_description}</p>
            )}
            {media?.photographer_credit && (
              <p className="text-xs text-room-muted italic">
                {media.photographer_credit}
              </p>
            )}
            {media?.ai_tags && media.ai_tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {media.ai_tags.slice(0, 6).map((t, i) => (
                  <span key={i} className="text-[10px] bg-room-bg border border-room-border rounded-md px-2 py-0.5 text-room-muted">
                    {t}
                  </span>
                ))}
              </div>
            )}
            <p className="text-[11px] text-room-muted pt-1">
              Captured {new Date(pick.created_at).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>

        {/* Context card */}
        <div className="bg-room-surface/50 border border-room-border rounded-2xl p-4 mb-5 text-xs text-room-muted leading-relaxed">
          <p>
            If you approve, {venue.name} may publish this shot on their own
            channels. If you decline or request changes, the shot will be
            held and not published. Your response is recorded with timestamp
            for the venue&rsquo;s audit trail.
          </p>
        </div>

        {/* Optional notes */}
        <label className="block mb-5">
          <span className="block text-xs uppercase tracking-widest text-room-muted font-semibold mb-1.5">
            Notes (optional)
          </span>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            disabled={submitting}
            rows={2}
            placeholder="e.g. caption must credit @claptone"
            className="w-full bg-room-surface border border-room-border rounded-xl px-4 py-3 text-sm text-white placeholder:text-room-muted focus:outline-none focus:border-white/40 disabled:opacity-50 resize-none"
          />
        </label>

        {/* Error */}
        {error && (
          <div className="bg-room-error/15 border border-room-error/40 rounded-xl p-3.5 text-sm text-room-error mb-4">
            {error}
          </div>
        )}

        {/* Decision buttons */}
        <div className="space-y-2.5">
          <button
            type="button"
            onClick={() => submit('approved')}
            disabled={submitting}
            className={cn(
              'w-full py-4 rounded-2xl font-bold text-base transition-all min-h-[48px]',
              'flex items-center justify-center gap-2',
              'bg-room-success text-black hover:bg-emerald-400',
              'disabled:opacity-50 disabled:cursor-wait'
            )}
          >
            <Check size={18} strokeWidth={2.5} />
            {submitting && chosenDecision === 'approved' ? 'Recording…' : 'Approve'}
          </button>

          <button
            type="button"
            onClick={() => submit('changes_requested')}
            disabled={submitting}
            className={cn(
              'w-full py-4 rounded-2xl font-semibold text-base transition-all min-h-[48px]',
              'flex items-center justify-center gap-2',
              'bg-room-warning/15 text-room-warning border border-room-warning/40 hover:bg-room-warning/25',
              'disabled:opacity-50 disabled:cursor-wait'
            )}
          >
            <MessageSquareWarning size={18} strokeWidth={2} />
            {submitting && chosenDecision === 'changes_requested' ? 'Recording…' : 'Request changes'}
          </button>

          <button
            type="button"
            onClick={() => submit('declined')}
            disabled={submitting}
            className={cn(
              'w-full py-4 rounded-2xl font-semibold text-base transition-all min-h-[48px]',
              'flex items-center justify-center gap-2',
              'bg-transparent text-room-muted border border-room-border hover:text-room-error hover:border-room-error/40',
              'disabled:opacity-50 disabled:cursor-wait'
            )}
          >
            <X size={18} strokeWidth={2} />
            {submitting && chosenDecision === 'declined' ? 'Recording…' : 'Decline'}
          </button>
        </div>

        {/* Expiry note */}
        <p className="text-center text-[11px] text-room-muted mt-5">
          This link expires {new Date(request.expires_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </p>

      </div>
    </div>
  )
}
